import { useEffect, useRef, useState } from 'react'
import { FileItem } from '../model'
import {
    deleteOrderDetailUploadFile,
    downloadOrderDetailUploadFile,
    getPresignedUrlandUploadToS3Directly,
    getSuborderUploadFiles
} from '../services/api/orders'
import { openDownloadUrl } from '../helpers/utils'
import { getApiErrorMessage } from '../utils/apiErrors'

const MAX_FILE_SIZE = 2147483648 // 2 GB

const uploadErrorMessages = {
    load: 'De bestanden konden niet worden geladen. Probeer het opnieuw.',
    upload: 'Het bestand kon niet worden geüpload. Probeer het opnieuw.',
    refresh: 'De bestandenlijst kon niet worden bijgewerkt. Vernieuw de pagina om de geüploade bestanden te zien.',
    delete: 'Het bestand kon niet worden verwijderd. Probeer het opnieuw.',
    download: 'Het bestand kon niet worden gedownload. Probeer het opnieuw.'
} as const

const getUploadErrorMessage = (error: unknown, action: keyof typeof uploadErrorMessages) =>
    getApiErrorMessage(error) || uploadErrorMessages[action]

export const useSuborderUploads = (orderId?: number, selectedProductId?: number) => {
    const [files, setFiles] = useState<FileItem[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [isUploading, setIsUploading] = useState(false)
    const [uploadCurrent, setUploadCurrent] = useState(0)
    const [uploadTotal, setUploadTotal] = useState(0)
    const [message, setMessage] = useState<string | null>(null)
    const selectedProductIdRef = useRef<number | undefined>(selectedProductId)

    useEffect(() => {
        selectedProductIdRef.current = selectedProductId
    }, [selectedProductId])

    const refreshFiles = async (productId: number) => {
        if (!orderId) return
        const response = await getSuborderUploadFiles(orderId, productId)
        if (selectedProductIdRef.current === productId) setFiles(response.files || [])
    }

    useEffect(() => {
        if (!orderId || selectedProductId === undefined) {
            setFiles([])
            setIsLoading(false)
            return
        }

        let cancelled = false
        const loadFiles = async () => {
            setIsLoading(true)
            setMessage(null)
            try {
                const response = await getSuborderUploadFiles(orderId, selectedProductId)
                if (!cancelled) setFiles(response.files || [])
            } catch (error) {
                if (!cancelled) {
                    setFiles([])
                    setMessage(getUploadErrorMessage(error, 'load'))
                }
            } finally {
                if (!cancelled) setIsLoading(false)
            }
        }

        loadFiles()
        return () => {
            cancelled = true
        }
    }, [orderId, selectedProductId])

    const uploadFiles = async (filesToUpload: File[]) => {
        if (!orderId || selectedProductId === undefined || !filesToUpload.length || isUploading) return

        const invalidFile = filesToUpload.find((file) => file.size < 1 || file.size > MAX_FILE_SIZE)
        if (invalidFile) {
            setMessage(`“${invalidFile.name}” moet tussen 1 byte en 2 GB groot zijn.`)
            return
        }

        const uploadProductId = selectedProductId
        setIsUploading(true)
        setUploadCurrent(0)
        setUploadTotal(filesToUpload.length)
        setMessage(null)
        let completed = 0
        let uploadsCompleted = false

        try {
            for (const file of filesToUpload) {
                setUploadCurrent(completed + 1)
                await getPresignedUrlandUploadToS3Directly(orderId, uploadProductId, file)
                completed += 1
            }
            uploadsCompleted = true
            await refreshFiles(uploadProductId)
        } catch (error) {
            if (selectedProductIdRef.current === uploadProductId) {
                const errorMessage = getUploadErrorMessage(error, uploadsCompleted ? 'refresh' : 'upload')
                setMessage(
                    uploadsCompleted
                        ? `${filesToUpload.length} bestand(en) zijn geüpload. ${errorMessage}`
                        : completed
                          ? `${completed} bestand(en) zijn geüpload. ${errorMessage}`
                          : errorMessage
                )
            }
            if (!uploadsCompleted) await refreshFiles(uploadProductId).catch(() => {})
        } finally {
            setIsUploading(false)
            setUploadCurrent(0)
            setUploadTotal(0)
        }
    }

    const deleteFile = async (file: FileItem) => {
        if (!orderId || selectedProductId === undefined) return
        const productId = selectedProductId
        try {
            setMessage(null)
            await deleteOrderDetailUploadFile(orderId, String(file.id))
            await refreshFiles(productId)
        } catch (error) {
            if (selectedProductIdRef.current === productId) {
                setMessage(getUploadErrorMessage(error, 'delete'))
            }
        }
    }

    const downloadFile = async (file: FileItem) => {
        if (!orderId) return
        const productId = selectedProductId
        try {
            setMessage(null)
            const { downloadUrl } = await downloadOrderDetailUploadFile(orderId, String(file.id))
            openDownloadUrl(downloadUrl)
        } catch (error) {
            if (selectedProductIdRef.current === productId) {
                setMessage(getUploadErrorMessage(error, 'download'))
            }
        }
    }

    return { files, isLoading, isUploading, uploadCurrent, uploadTotal, message, uploadFiles, deleteFile, downloadFile }
}
